import { Box, Flex, Text } from '@mantine/core'
import HelpTooltip from 'common/HelpTooltip'
import dayjs from 'dayjs'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { MRT_Localization_PL } from 'mantine-react-table/locales/pl/index.cjs'
import { useMemo } from 'react'
import { OrderWithHistory } from 'types/orders'

type Props = {
  data: Array<OrderWithHistory>
  onRowClick: (order: OrderWithHistory) => void
}

export default function OrderHistorySelectTable({ data, onRowClick }: Props) {
  const columns = useMemo<Array<MRT_ColumnDef<OrderWithHistory>>>(
    () => [
      {
        accessorKey: 'order.id',
        header: 'Nr zamówienia',
        size: 150,
      },
      {
        accessorKey: 'order.createdAt',
        header: 'Data dodania',
        Cell: ({ cell }) => {
          const createdAt = cell.getValue<Date | null>()
          if (createdAt) {
            return dayjs(createdAt).format('D MMMM HH:mm')
          } else {
            // find the earliest date in history
            const row = cell.row.original
            const earliestHistoryDate = row.history.reduce(
              (earliest, current) => (current.date < earliest ? current.date : earliest),
              new Date()
            )
            return (
              <Flex align="center" gap="xs">
                {dayjs(earliestHistoryDate).format('D MMMM HH:mm')}{' '}
                <HelpTooltip text="Data utworzenia zamówienia nie jest dostępna. Wyświetlana data to data najwcześniejszej pozycji w historii zamówienia." />
              </Flex>
            )
          }
        },
        size: 220,
      },
      {
        accessorKey: 'order.firstName',
        header: 'Imię i nazwisko',
        size: 200,
        Cell: ({ cell }) => {
          const firstName = cell.getValue<string | null>()
          if (!firstName) return '-'
          const row = cell.row.original
          return <Text size="sm">{`${firstName} ${row.order.lastName}`}</Text>
        },
      },
      {
        accessorKey: 'order.email',
        header: 'Email',
        Cell: ({ cell }) => {
          const email = cell.getValue<string | null>()
          if (!email) return '-'
          return (
            <Box w={270}>
              <Text truncate="end" size="sm" style={{ textTransform: 'lowercase' }}>
                {email}
              </Text>
            </Box>
          )
        },
        size: 300,
      },
    ],
    []
  )

  const table = useMantineReactTable({
    columns,
    data,
    layoutMode: 'grid',
    enableSorting: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableTopToolbar: false,
    initialState: {
      pagination: { pageSize: 10, pageIndex: 0 },
    },
    enableBottomToolbar: data && data.length >= 10 ? true : false,
    paginationDisplayMode: 'pages',
    localization: MRT_Localization_PL,
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: () => onRowClick(row.original),
      sx: {
        cursor: 'pointer',
      },
    }),
  })

  return <MantineReactTable table={table} />
}
